import React from "react";
import { ICustomerEventSource } from "../../../vm";
import CustomDrawer from "../../common/CustomDrawer";
import * as yup from "yup";
import {
  DefaultButton,
  IBasePickerSuggestionsProps,
  IStackTokens,
  ITag,
  PrimaryButton,
  Stack,
  TagPicker,
} from "office-ui-fabric-react";
import {
  getEventSources,
  updateCustomerEventSources,
} from "../../../services/customers/CustomerService";
import { Formik } from "formik";
import { ToastContext } from "../../common/ToastProvider";

const eSSchema = yup.object().shape({
  datacenterId: yup.string().required("required"),
});

export interface ManageEventSourceDialogProps {
  data?: ICustomerEventSource;
  handleLoading: any;
  handleDialogClose: any;
  customerId: string;
  customerEventSources: ICustomerEventSource[];
  dataCenterList: ITag[];
}

const ManageEventSourceDialog: React.FC<ManageEventSourceDialogProps> = ({
  data,
  handleDialogClose,
  handleLoading,
  customerId,
  customerEventSources,
  dataCenterList,
}) => {
  //state
  const [state, setState] = React.useState({
    customerEventSources: {
      datacenterId: "",
      customerId: customerId,
      eventSourceIds: [],
      eventSources: [],
      ...data,
    },
    selectedDatacenter: [],
    selectedEventSources: [],
  } as {
    customerEventSources: ICustomerEventSource;
    selectedDatacenter: ITag[];
    selectedEventSources: ITag[];
  });

  //hooks
  React.useEffect(() => {
    if (data && data.datacenterId !== "") {
      let selectedDatacenter: ITag[] = [];
      dataCenterList.forEach((ele) => {
        if (ele.key === data.datacenterId) {
          selectedDatacenter.push(ele);
        }
      });
      let selectedEventSources: ITag[] = [];
      if (data.eventSourceIds.length > 0) {
        data.eventSources.forEach((es) => {
          selectedEventSources.push({
            key: es.id,
            name: es.name,
          });
        });
        // console.log("selecteddatacenter", selectedDatacenter);
      }
      setState({
        ...state,
        selectedDatacenter,
        selectedEventSources,
      });
    }
  }, []);

  //custom hooks
  const { showToast } = React.useContext(ToastContext);

  const wrapStackTokens: IStackTokens = { childrenGap: 32 };

  const closeAddDialog = (data?: ICustomerEventSource) => {
    handleDialogClose(data);
  };

  const eventSourcePickerSuggestionsProps: IBasePickerSuggestionsProps = {
    // suggestionsHeaderText: "Suggested Datacenters",
    // mostRecentlyUsedHeaderText: "Suggested Datacenters",
    noResultsFoundText: "No event sources found",
  };

  const datacenterPickerSuggestionsProps: IBasePickerSuggestionsProps = {
    // suggestionsHeaderText: "Suggested Datacenters",
    // mostRecentlyUsedHeaderText: "Suggested Datacenters",
    noResultsFoundText: "No datacenter found",
  };

  const filterSuggestedTags = (filterText: string, tagList: ITag[]): ITag[] => {
    let allCustomerEventSources = [...customerEventSources];
    let allDataCenterList = [...dataCenterList];

    allCustomerEventSources.forEach((item) => {
      allDataCenterList = allDataCenterList.filter(
        (ele) => ele.key !== item.datacenterId
      );
    });

    return filterText
      ? allDataCenterList.filter(
          (tag) =>
            tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
            !listContainsTagList(tag, tagList)
        )
      : [];
  };

  const showAllTags = (selectedItems?: ITag[] | undefined) => {
    let allCustomerEventSources = [...customerEventSources];
    let allDataCenterList = [...dataCenterList];

    allCustomerEventSources.forEach((item) => {
      allDataCenterList = allDataCenterList.filter(
        (ele) => ele.key !== item.datacenterId
      );
    });

    console.log(allDataCenterList);
    return removeDuplicates(allDataCenterList, selectedItems);
  };

  function removeDuplicates(tagList: ITag[], tags?: ITag[]) {
    return tagList.filter((tag) => !listContainsTagList(tag, tags));
  }

  const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.some((compareTag) => compareTag.key === tag.key);
  };

  // const getTextFromItem = (item: ITag) => item.name;

  const onFilterChanged = async (
    filterText: string,
    tagList?: ITag[]
  ): Promise<ITag[]> => {
    if (filterText && filterText.length >= 2) {
      let result = await getEventSources({
        search: filterText,
        customerId: customerId,
        // isNewAssign: true,
      });
      if (result?.success) {
        let eventSourceTag: ITag[] = [];

        result?.data.forEach((item) => {
          eventSourceTag.push({
            key: item._id as string,
            name: item.name,
          });
        });
        tagList?.forEach((tag) => {
          eventSourceTag = eventSourceTag.filter((es) => es.key !== tag.key);
        });
        return eventSourceTag;
      } else {
        showToast(
          result.message || "Error while getting event source details",
          "error"
        );
        return [];
      }
    }
    return [];
  };

  return (
    <React.Fragment>
      <CustomDrawer
        title={
          state.customerEventSources._id
            ? "Update Event Sources"
            : "Add Event Sources"
        }
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            enableReinitialize
            initialValues={state.customerEventSources}
            validationSchema={eSSchema}
            onSubmit={async (
              values: ICustomerEventSource,
              { setSubmitting }
            ) => {
              let obj = { ...values };
              let result;
              handleLoading();
              result = await updateCustomerEventSources(obj);
              handleLoading();
              if (result.success) {
                closeAddDialog(result.data);
              } else {
                showToast(
                  result.message ||
                    "Error while adding or updating event source",
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, setFieldValue }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={wrapStackTokens} className="pt-16">
                  <Stack.Item>
                    <TagPicker
                      removeButtonAriaLabel="Remove"
                      itemLimit={1}
                      defaultSelectedItems={state.selectedDatacenter}
                      onResolveSuggestions={filterSuggestedTags as any}
                      pickerSuggestionsProps={datacenterPickerSuggestionsProps}
                      onEmptyResolveSuggestions={showAllTags}
                      // getTextFromItem={getTextFromItem}
                      inputProps={{
                        placeholder: "Select a particular datacenter",
                      }}
                      disabled={state.customerEventSources._id !== undefined}
                      onChange={(items: any) => {
                        let datacenterId: string = "";
                        items.forEach((ele: any) => {
                          datacenterId = ele.key;
                        });
                        setFieldValue("datacenterId", datacenterId);
                        // setState({
                        //   ...state,
                        //   selectedDatacenters: items,
                        //   customerEventSources: {
                        //     ...state.customerEventSources,
                        //     datacenterId,
                        //   },
                        // });
                      }}
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <TagPicker
                      inputProps={{
                        placeholder: "Search event sources by name...",
                      }}
                      removeButtonAriaLabel="Remove"
                      defaultSelectedItems={state.selectedEventSources}
                      onResolveSuggestions={onFilterChanged}
                      pickerSuggestionsProps={eventSourcePickerSuggestionsProps}
                      onChange={(items: any) => {
                        let eventSourceIds: any = [];
                        let eventSources: any = [];
                        items.forEach((ele: any) => {
                          eventSourceIds.push(ele.key);
                          eventSources.push(ele);
                        });
                        setFieldValue("eventSourceIds", eventSourceIds);
                        setFieldValue("eventSources", eventSources);
                        // setState({
                        //   ...state,
                        //   customerEventSources: {
                        //     ...state.customerEventSources,
                        //     eventSourceIds,
                        //   },
                        // });
                      }}
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      horizontalAlign="end"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeAddDialog()}
                        className="borderless-btn"
                      />
                      <PrimaryButton
                        type="submit"
                        text={state.customerEventSources._id ? "Update" : "Add"}
                      />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageEventSourceDialog;
